import { cn } from '@/lib/utils/cn';

/**
 * Progress bar + completed/remaining counts for the active session.
 *
 * Pure presentational. The parent computes the numbers; this component
 * just renders them.
 */

type SessionProgressProps = {
  /** How many ayahs the user has marked as memorized. */
  memorizedCount: number;
  /** Total ayahs in the session range (inclusive). */
  totalCount: number;
  /** Current ayah number (within the surah). */
  currentAyah: number;
  /** First ayah of the range. */
  startAyah: number;
  /** Last ayah of the range. */
  endAyah: number;
};

export function SessionProgress({
  memorizedCount,
  totalCount,
  currentAyah,
  startAyah,
  endAyah,
}: SessionProgressProps) {
  const memorizedPct =
    totalCount > 0 ? Math.round((memorizedCount / totalCount) * 100) : 0;
  // Position progress (independent of memorized count) — where in the
  // range the cursor is right now.
  const positionPct =
    totalCount > 0
      ? Math.round(((currentAyah - startAyah) / Math.max(totalCount - 1, 1)) * 100)
      : 0;
  const remaining = Math.max(totalCount - memorizedCount, 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-xs">
        <span className="text-ink-500 dark:text-ink-400">
          Range {startAyah}–{endAyah} · current {currentAyah}
        </span>
        <span className="text-ink-700 dark:text-ink-200 font-medium num-tight">
          {memorizedCount} of {totalCount} memorized · {remaining} remaining
        </span>
      </div>

      <div
        role="progressbar"
        aria-valuenow={memorizedPct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Memorization progress"
        className="relative h-2.5 rounded-full bg-ink-100 dark:bg-ink-800 overflow-hidden"
      >
        {/* Memorized fill — solid accent */}
        <div
          className="absolute inset-y-0 left-0 bg-accent transition-[width] duration-300 ease-smooth"
          style={{ width: `${memorizedPct}%` }}
        />
        {/* Position marker — a thin vertical line at the cursor */}
        <div
          aria-hidden="true"
          className={cn(
            'absolute top-0 bottom-0 w-0.5',
            'bg-ink-900 dark:bg-ink-100 opacity-60',
            'transition-[left] duration-300 ease-smooth',
          )}
          style={{ left: `${positionPct}%` }}
        />
      </div>
    </div>
  );
}
